import { useEffect, useRef, useState } from 'react';
import { api, type Grp, type NoticeAdminRow, type NoticePushPreview } from '../lib/api';
import { ErrorBox } from '../components/DataTable';
import Modal from '../components/Modal';
import { FitBox } from '../components/FitBox';
import NoticeBody from '../driver/NoticeBody';
import NoticeEditor from '../components/NoticeEditor';
import { canScreen, useMe } from '../lib/me';

/**
 * 공지 관리(2026-09-27 way 「어드민에서 공지사항 보내는 메뉴가 없네」).
 *
 * 쓰기·고치기·내리기 + 원하면 기사 폰으로 알림. 기사앱 공지사항에 그대로 뜬다(대상 소속을 고르면 — 여러 개 가능 — 그 소속 기사만).
 * 🔴 알림은 **보내기 전에 받을 인원을 먼저 보여 주고** 확인받는다(`previewNoticePush`). 서버가 보낼 때 다시 세어
 *    확인한 숫자와 다르면 거절한다. 같은 공지를 두 번 보내려 하면 한 번 더 묻는다.
 * 권한: `배차`(dispatch) 이상. 조회 전용 계정에는 메뉴가 보여도 버튼이 없다.
 */

const fmt = (s: string | null | undefined) => (s ? s.slice(0, 16).replace('T', ' ') : '-');

export default function NoticeAdmin() {
  const { me } = useMe();
  const canWrite = canScreen(me, 'notices');
  const [rows, setRows] = useState<NoticeAdminRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [pushFor, setPushFor] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listNoticesAdmin({ q: q.trim() || undefined, includeExpired })
      .then((r) => !cancelled && setRows(r.rows))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [q, includeExpired, nonce]);

  async function remove(r: NoticeAdminRow) {
    if (!window.confirm(`「${r.subject ?? ''}」 공지를 내릴까요?\n기사앱 공지사항에서 사라집니다.`)) return;
    try {
      await api.deleteNotice({ noticeId: r.notice_id });
      setMessage('공지를 내렸습니다.');
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="page-head min-w-0 flex-1">
          <h1 className="font-bold">공지사항</h1>
          <p className="text-sm text-ink-soft">기사앱 공지사항에 뜹니다. 대상 소속을 고르면(여러 개 가능) 그 소속 기사에게만 보입니다.</p>
        </div>
        {canWrite && (
          <button className="btn-primary" onClick={() => setEditing('new')}>
            ＋ 새 공지
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input className="field max-w-xs" placeholder="제목 검색" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={includeExpired} onChange={(e) => setIncludeExpired(e.target.checked)} />
          게시가 끝난 공지도 보기
        </label>
      </div>

      {message && <div className="card border-green-200 bg-green-50 p-3 text-sm text-green-800">{message}</div>}
      {error && <ErrorBox message={error} />}

      <FitBox deps={[rows.length, loading, !!message]}>
        <table className="w-full">
          <thead>
            <tr>
              <th className="th">제목</th>
              <th className="th">대상</th>
              <th className="th">올린 날</th>
              <th className="th">게시 종료</th>
              <th className="th text-right">기사 읽음</th>
              <th className="th">폰 알림</th>
              <th className="th" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.notice_id} className={r.is_active ? '' : 'text-ink-faint'}>
                <td className="td max-w-[28rem] truncate">
                  {r.is_top && <span className="mr-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700">상단</span>}
                  {!r.is_active && <span className="mr-1.5 rounded bg-canvas px-1.5 py-0.5 text-xs">게시 끝</span>}
                  <button className="text-left hover:underline" onClick={() => setEditing(r.notice_id)}>
                    {r.subject || '(제목 없음)'}
                  </button>
                </td>
                <td className="td">{r.grp_name || '전체 기사'}</td>
                <td className="td">{fmt(r.created_at)}</td>
                <td className="td">{r.expire || '없음'}</td>
                <td className="td num">{r.driver_read_cnt.toLocaleString()}명</td>
                <td className="td">
                  {r.last_push_at ? (
                    <span className="text-xs text-ink-soft">
                      {fmt(r.last_push_at)} · {r.last_push_success ?? 0}대
                    </span>
                  ) : (
                    <span className="text-xs text-ink-faint">안 보냄</span>
                  )}
                  {canWrite && r.is_active && (
                    <button className="btn-ghost ml-2 px-2 py-1 text-xs" onClick={() => setPushFor(r.notice_id)}>
                      {r.last_push_at ? '다시 보내기' : '알림 보내기'}
                    </button>
                  )}
                </td>
                <td className="td text-right">
                  {canWrite && (
                    <button className="text-xs text-red-600 hover:underline" onClick={() => void remove(r)}>
                      내리기
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="td py-10 text-center text-ink-faint" colSpan={7}>
                  공지가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </FitBox>

      {editing !== null && (
        <Modal width="max-w-3xl">
          <NoticeForm
            noticeId={editing === 'new' ? null : editing}
            canWrite={canWrite}
            onClose={() => setEditing(null)}
            onSaved={(id, push) => {
              setEditing(null);
              setMessage('저장했습니다. 기사앱 공지사항에 바로 뜹니다.');
              setNonce((n) => n + 1);
              if (push) setPushFor(id);
            }}
          />
        </Modal>
      )}
      {pushFor !== null && (
        <Modal width="max-w-md">
          <PushConfirm
            noticeId={pushFor}
            onClose={() => setPushFor(null)}
            onSent={(msg) => {
              setPushFor(null);
              setMessage(msg);
              setNonce((n) => n + 1);
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function NoticeForm({
  noticeId,
  canWrite,
  onClose,
  onSaved,
}: {
  noticeId: number | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: (noticeId: number, push: boolean) => void;
}) {
  const [grps, setGrps] = useState<Grp[]>([]);
  const [subject, setSubject] = useState('');
  const [comment, setComment] = useState('');
  const [grpIds, setGrpIds] = useState<number[]>([]);
  const [isTop, setIsTop] = useState(false);
  const [expire, setExpire] = useState('');
  const [push, setPush] = useState(noticeId === null);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(noticeId !== null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.listGrps({ limit: 200 }).then((r) => setGrps(r.rows)).catch(() => undefined);
    if (noticeId === null) return;
    api
      .getNoticeAdmin({ noticeId })
      .then(({ row }) => {
        setSubject(row.subject ?? '');
        setComment(row.comment ?? '');
        setGrpIds(row.grp_ids ?? []);
        setIsTop(row.is_top === true);
        setExpire(row.expire ?? '');
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [noticeId]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.saveNotice({
        noticeId,
        subject,
        comment,
        grpIds,
        isTop,
        expire: expire || null,
      });
      onSaved(r.noticeId, push);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4 p-6">
      <h2 className="text-base font-bold">{noticeId === null ? '새 공지' : '공지 고치기'}</h2>
      {loading ? (
        <p className="text-sm text-ink-soft">불러오는 중…</p>
      ) : (
        <>
          <label className="block text-sm">
            <span className="mb-1 block font-semibold">제목</span>
            <input className="field" value={subject} maxLength={500} onChange={(e) => setSubject(e.target.value)} disabled={!canWrite} />
          </label>
          <div className="grid gap-4 sm:grid-cols-3">
            {/* label 이 아니라 div — 안의 네모 label 들을 바깥 label 이 되쏘지 않게(issues 103). */}
            <div className="block text-sm">
              <span className="mb-1 block font-semibold">대상</span>
              <GrpMultiPicker grps={grps} value={grpIds} onChange={setGrpIds} disabled={!canWrite} />
              <span className="mt-1 block text-xs text-ink-faint">여러 소속을 고를 수 있습니다</span>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block font-semibold">게시 종료일</span>
              <input className="field" type="date" value={expire} onChange={(e) => setExpire(e.target.value)} disabled={!canWrite} />
              <span className="mt-1 block text-xs text-ink-faint">비우면 계속 게시</span>
            </label>
            <label className="mt-6 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isTop} onChange={(e) => setIsTop(e.target.checked)} disabled={!canWrite} />
              맨 위에 고정
            </label>
          </div>
          {/* label 이 아니라 div — 편집기 버튼 누름을 label 이 다른 칸으로 되쏘지 않게(issues 103). */}
          <div className="block text-sm">
            <span className="mb-1 flex items-center justify-between font-semibold">
              내용
              <button type="button" className="text-xs font-normal text-brand-600 hover:underline" onClick={() => setPreview((v) => !v)}>
                {preview ? '고치기로 돌아가기' : '기사 화면으로 미리보기'}
              </button>
            </span>
            {preview ? (
              <div className="mx-auto max-w-sm rounded-2xl border border-line bg-surface p-5">
                <NoticeBody text={comment} />
              </div>
            ) : (
              <NoticeEditor initial={comment} onChange={setComment} disabled={!canWrite} />
            )}
          </div>
          {noticeId === null && canWrite && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} />
              저장한 뒤 기사 폰으로 알림 보내기 <span className="text-ink-faint">(받을 인원을 먼저 보여 드립니다)</span>
            </label>
          )}
        </>
      )}
      {error && <ErrorBox message={error} />}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>
          {canWrite ? '취소' : '닫기'}
        </button>
        {canWrite && (
          <button className="btn-primary" onClick={() => void save()} disabled={busy || loading || !subject.trim() || !comment.trim()}>
            {busy ? '저장 중…' : '저장'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 대상 소속 여러 개 고르기(2026-10-19 way 「공지 보낼 때 소속 복수로 선택」).
 * 아무것도 안 고르면 = 전체 기사. 고른 순서는 소속 목록 순서로 맞춘다(알림 제목 「[동영(서울)·동영(용인)] 새 공지」).
 */
function GrpMultiPicker({
  grps,
  value,
  onChange,
  disabled,
}: {
  grps: Grp[];
  value: number[];
  onChange: (ids: number[]) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const picked = grps.filter((g) => value.includes(g.grp_id)).map((g) => g.grp_name ?? '');
  const label =
    value.length === 0 ? '전체 기사' : picked.length <= 2 ? picked.join(', ') : `${picked[0]} 외 ${picked.length - 1}곳`;
  const toggle = (id: number) => {
    const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
    const order = new Map(grps.map((g, i) => [g.grp_id, i]));
    onChange(next.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)));
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        className="field flex items-center justify-between gap-2 text-left"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={picked.join(', ')}
      >
        <span className="truncate">{label}</span>
        <span className="shrink-0 text-ink-faint">▾</span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-line bg-surface p-1 shadow-lg">
          <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-canvas">
            <input type="checkbox" checked={value.length === 0} onChange={() => onChange([])} />
            <span className="font-semibold">전체 기사</span>
          </label>
          {grps.map((g) => (
            <label key={g.grp_id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-canvas">
              <input type="checkbox" checked={value.includes(g.grp_id)} onChange={() => toggle(g.grp_id)} />
              {g.grp_name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function PushConfirm({ noticeId, onClose, onSent }: { noticeId: number; onClose: () => void; onSent: (msg: string) => void }) {
  const [p, setP] = useState<NoticePushPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .previewNoticePush({ noticeId })
      .then(setP)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [noticeId]);

  async function send() {
    if (!p) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.sendNoticePush({ noticeId, expectTokens: p.tokenCnt, again: p.lastSentAt !== null });
      onSent(
        `알림을 보냈습니다 — 폰 ${r.tokenCnt}대 중 ${r.success}대 성공` +
          (r.fail ? ` · 실패 ${r.fail}대` : '') +
          (r.removed ? ` (앱을 지운 폰 ${r.removed}대는 목록에서 뺐습니다)` : '') +
          '.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4 p-6">
      <h2 className="text-base font-bold">기사 폰으로 알림 보내기</h2>
      {!p && !error && <p className="text-sm text-ink-soft">받을 인원을 세는 중…</p>}
      {p && (
        <div className="space-y-2 text-sm">
          <p>
            대상 <b>{p.target}</b>
          </p>
          <p className="text-lg font-bold">
            기사 {p.driverCnt.toLocaleString()}명 · 폰 {p.tokenCnt.toLocaleString()}대
          </p>
          <p className="text-ink-soft">
            대상 기사 {p.allDriverCnt.toLocaleString()}명 중 기사앱에서 <b>알림을 허용한 분</b>에게만 갑니다. 나머지 분은 앱을 열면 공지사항에서 봅니다.
          </p>
          {p.lastSentAt && (
            <p className="rounded-lg bg-amber-50 p-3 text-amber-800">
              이 공지는 {fmt(p.lastSentAt)}에 이미 알림을 보냈습니다({p.lastSuccess ?? 0}대). 다시 보내면 같은 분들이 한 번 더 받습니다.
            </p>
          )}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>
          {p && p.tokenCnt > 0 ? '보내지 않기' : '닫기'}
        </button>
        {p && p.tokenCnt > 0 && (
          <button className="btn-primary" onClick={() => void send()} disabled={busy}>
            {busy ? '보내는 중…' : p.lastSentAt ? '한 번 더 보내기' : `${p.tokenCnt.toLocaleString()}대에 보내기`}
          </button>
        )}
      </div>
    </div>
  );
}
